// Thin client for /api/plan-zones and /api/handoff. Uses the shared designer
// JWT stored in localStorage.authToken (same pattern as services/api.js and
// elsewhere in PlanDetail). Kept separate so the handoff UI can live in its
// own folder without pulling in the bigger `services/api.js` surface.

// Same base-URL rule as DesignerDashboard / gardenApi: on Vercel the Express
// app is mounted at /api (see api/index.js); only use localhost in dev when
// the CRA dev server proxies or you run the API on 3001 separately.
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

function authHeaders(extra = {}) {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('authToken')) || '';
  return {
    Authorization: `Bearer ${token}`,
    ...extra,
  };
}

async function handle(res) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const err = new Error((body && body.error) || (typeof body === 'string' ? body : `Request failed (${res.status})`));
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export const planZonesApi = {
  list: async (projectId, { planId = null } = {}) => {
    const qs = planId ? `?planId=${encodeURIComponent(planId)}` : '';
    const res = await fetch(`${API_BASE_URL}/plan-zones/project/${projectId}${qs}`, {
      headers: authHeaders(),
    });
    return handle(res);
  },
  create: async (payload) => {
    const res = await fetch(`${API_BASE_URL}/plan-zones`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  update: async (id, patch) => {
    const res = await fetch(`${API_BASE_URL}/plan-zones/${id}`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(patch),
    });
    return handle(res);
  },
  remove: async (id) => {
    const res = await fetch(`${API_BASE_URL}/plan-zones/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return handle(res);
  },
};

export const handoffApi = {
  preview: async ({ projectId, planId = null, homeownerEmail = null }) => {
    const res = await fetch(`${API_BASE_URL}/handoff/preview`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ projectId, planId, homeownerEmail }),
    });
    return handle(res);
  },
  send: async (payload) => {
    const res = await fetch(`${API_BASE_URL}/handoff/send`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    return handle(res);
  },
  publicFetch: async (token) => {
    const res = await fetch(`${API_BASE_URL}/handoff/public/${encodeURIComponent(token)}`);
    return handle(res);
  },
  accept: async (token) => {
    const res = await fetch(`${API_BASE_URL}/handoff/accept`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token }),
    });
    return handle(res);
  },
  cancel: async (id) => {
    const res = await fetch(`${API_BASE_URL}/handoff/${id}/cancel`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  listForProject: async (projectId) => {
    const res = await fetch(`${API_BASE_URL}/handoff/project/${projectId}`, {
      headers: authHeaders(),
    });
    return handle(res);
  },
};

export const masterListAiApi = {
  fillItem: async (projectId, itemId) => {
    const res = await fetch(`${API_BASE_URL}/plans/${projectId}/master-list/${itemId}/ai-fill`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return handle(res);
  },
  fillBulk: async (projectId, { itemIds = null, onlyMissing = true } = {}) => {
    const res = await fetch(`${API_BASE_URL}/plans/${projectId}/master-list/ai-fill-bulk`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ itemIds, onlyMissing }),
    });
    return handle(res);
  },
};
